import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { NAV, COMPANY } from "@/content/site";
import { scrollToId, lockScroll } from "@/lib/scroll";
import { EASE } from "./Reveal";

export const useGoTo = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  return (id) => {
    if (pathname === "/") scrollToId(id);
    else navigate(`/#${id}`);
  };
};

const MobileMenu = ({ open, onClose, goTo }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        data-testid="mobile-menu"
        className="fixed inset-0 z-40 flex flex-col bg-[#1E2228] px-6 pb-10 pt-28 lg:hidden"
        initial={{ clipPath: "inset(0 0 100% 0)" }}
        animate={{ clipPath: "inset(0 0 0% 0)" }}
        exit={{ clipPath: "inset(0 0 100% 0)" }}
        transition={{ duration: 0.6, ease: EASE }}
      >
        <nav className="flex flex-col gap-2" aria-label="Menú móvil">
          {NAV.map((n, i) => (
            <motion.a
              key={n.id}
              href={`/#${n.id}`}
              data-testid={`mobile-nav-${n.id}`}
              onClick={(e) => { e.preventDefault(); onClose(); setTimeout(() => goTo(n.id), 80); }}
              className="flex items-baseline gap-4 border-b border-white/10 py-4 text-left font-display text-3xl font-light text-white"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + i * 0.06, duration: 0.6, ease: EASE }}
            >
              <span className="font-serif-it text-lg text-[#C9A66B]">0{i + 1}</span>
              {n.label}
            </motion.a>
          ))}
        </nav>
        <div className="mt-auto flex flex-col gap-3">
          <a href={COMPANY.phoneHref} data-testid="mobile-menu-call" className="btn-gold"><Phone className="h-4 w-4" />Llamar {COMPANY.phone}</a>
          <button data-testid="mobile-menu-quote" onClick={() => { onClose(); goTo("contacto"); }} className="btn-ghost-light">Solicitar presupuesto</button>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

export default function Header() {
  const { pathname } = useLocation();
  const goTo = useGoTo();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const solid = scrolled || pathname !== "/";
  const light = !solid || open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    lockScroll(open);
    return () => lockScroll(false);
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${
        solid && !open ? "bg-[#F6F4EF]/90 py-3 shadow-[0_1px_0_rgba(30,34,40,0.08)] backdrop-blur-xl" : "bg-transparent py-5"
      }`}
    >
      <div className="container-x flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" aria-label="ASSE DECOR Interiorismo, ir al inicio" onClick={() => { setOpen(false); if (pathname === "/") window.scrollTo({ top: 0, behavior: "smooth" }); }}>
          <Logo light={light} />
        </Link>
        <nav className="hidden items-center gap-9 lg:flex" aria-label="Navegación principal">
          {NAV.map((n) => (
            <a key={n.id} href={`/#${n.id}`} data-testid={`nav-${n.id}`} onClick={(e) => { e.preventDefault(); goTo(n.id); }} className={`link-underline font-display text-[11px] font-semibold uppercase tracking-[0.24em] ${light ? "text-white/90" : "text-[#1E2228]"}`}>
              {n.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a href={COMPANY.phoneHref} data-testid="header-call-link" className={`hidden items-center gap-2 font-display text-xs font-semibold tracking-[0.12em] xl:flex ${light ? "text-white" : "text-[#1E2228]"}`}>
            <Phone className="h-3.5 w-3.5" />{COMPANY.phone}
          </a>
          <button data-testid="header-quote-button" onClick={() => goTo("contacto")} className="btn-gold hidden !px-5 !py-3 md:inline-flex">Presupuesto</button>
          <button
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className={`grid h-11 w-11 place-items-center border lg:hidden ${light ? "border-white/40 text-white" : "border-[#1E2228]/25 text-[#1E2228]"}`}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <MobileMenu open={open} onClose={() => setOpen(false)} goTo={goTo} />
    </header>
  );
}
